/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, createContext, useContext } from 'react';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Collapse, IconButton, Tooltip } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
	Settings,
	HelpCircle,
	FileBarChart,
	UserCircle,
	Shield,
	User,
	ChevronLeft,
	ChevronRight,
	Home,
	Info,
	Menu,
	Hammer,
	Banknote,
	ListChecks,
	Building2,
	Infinity as ProvisionIcon,
	Globe,
	BookUser,
	ScrollText,
	Users,
	BookOpenCheck,
	FileChartColumn,
	FileText,
	CreditCard,
	RotateCcw,
	PackagePlus,
	Lock
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Triquetra from './icons/triquetra';
import { useFeatures } from '../providers/features-provider';
import { usePermissions } from '../providers/permissions-provider';
import { useColorScheme } from '../providers/color-scheme-provider';

// Import menu JSON
import menuData from '../files/json/sidebar-menu.json';
import adminMenuData from '../files/json/admin-sidebar-menu.json';

// Import logo.png
import logoImage from '../files/images/logo.png';
import logoDarkImage from '../files/images/logo-dark.png';

// Map icon string to MUI icon component (not JSX)
const iconComponentMap: Record<string, React.ElementType> = {
	dashboard: ProvisionIcon,
	transaction: Triquetra,
	settings: Settings,
	help: HelpCircle,
	reports: FileBarChart,
	profile: UserCircle,
	users: Users,
	account: User,
	security: Shield,
	home: Home,
	info: Info,
	menu: Menu,
	build: Hammer,
	provision: Banknote,
	category: ListChecks,
	taxdefinition: FileBarChart,
	bankdefintion: Banknote,
	company: Building2,
	country: Globe,
	receipt: ScrollText,
	client: BookUser,
	bookOpenCheck: BookOpenCheck,
	fileChartColumn: FileChartColumn,
	document: FileText,
	addon: PackagePlus,
	creditcard: CreditCard,
	refund: RotateCcw,
};

type MenuItem = {
	label: string;
	icon?: string;
	path?: string;
	featureKey?: string;
	// A plan feature (e.g. Documents) - shown greyed out with a lock when the
	// company's plan doesn't have it, instead of hidden.
	planGated?: boolean;
	locked?: boolean;
	resourceKey?: string;
	children?: MenuItem[];
};

export const SIDEBAR_WIDTH = 250;
export const SIDEBAR_WIDTH_COMPRESSED = 64;

type SidebarContextType = {
	compressed: boolean;
	setCompressed: React.Dispatch<React.SetStateAction<boolean>>;
};
export const SidebarContext = createContext<SidebarContextType | undefined>(undefined);
export const useSidebarContext = () => {
	const ctx = useContext(SidebarContext);
	if (!ctx) throw new Error('useSidebarContext must be used within SidebarContext.Provider');
	return ctx;
};


const Sidebar: React.FC = () => {
	const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
	const { compressed, setCompressed } = useSidebarContext();
	const [popupMenu, setPopupMenu] = useState<{ anchorEl: HTMLElement | null, children: MenuItem[] } | null>(null);
	const navigate = useNavigate();
	const location = useLocation();
	const { isVisibleInNav } = useFeatures();
	const { can } = usePermissions();
	const { isDark } = useColorScheme();

	const isAdmin = location.pathname.includes('admin');

	const filterByFeature = (items: MenuItem[]): MenuItem[] =>
		items
			.filter(item => !item.featureKey || item.planGated || isVisibleInNav(item.featureKey))
			.map(item => (item.planGated && item.featureKey && !isVisibleInNav(item.featureKey) ? { ...item, locked: true } : item))
			.map(item => item.children
				? { ...item, children: filterByFeature(item.children) }
				: item
			);

	// Items without a resourceKey (e.g. reporting pages that don't map to a single
	// resource) are always shown; groups whose children are all filtered out collapse away
	const filterByPermission = (items: MenuItem[]): MenuItem[] =>
		items
			.map(item => item.children
				? { ...item, children: filterByPermission(item.children) }
				: item
			)
			.filter(item => {
				if (item.children) return item.children.length > 0;
				if (!item.resourceKey) return true;
				return can(item.resourceKey, 'read');
			});

	// Handle expand/collapse for menus with children
	const handleToggle = (label: string) => {
		setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
	};

	// Handle compressed popup menu
	const handleCompressedMenuClick = (event: React.MouseEvent<HTMLElement>, children: MenuItem[]) => {
		setPopupMenu({ anchorEl: event.currentTarget, children });
	};
	const handlePopupClose = () => setPopupMenu(null);

	// Colors
	// The admin portal keeps its own dark sidebar - not a company's colour scheme.
	const activeBg = isAdmin ? '#11c2a6' : 'var(--color-primary)';
	const activeColor = '#fff';
	const inactiveColor = isAdmin ? '#fff' : 'var(--color-default-text)';
	const bgColor = isAdmin ? '#323232' : 'var(--color-page-background)';

	// Render menu recursively
	const renderMenu = (items: MenuItem[], depth = 0) => (
		<List disablePadding>
			{items.map((item) => {
				const isActive = item.path && location.pathname.startsWith(item.path);
				const hasChildren = !!item.children?.length;
				const iconKey = item.icon;
				const IconComponent = iconKey && iconComponentMap[iconKey] ? iconComponentMap[iconKey] : Menu;
				
				return (
					<React.Fragment key={item.label}>
						<Tooltip title={item.locked ? 'Please upgrade to access' : compressed ? item.label : ''} placement="right" arrow disableHoverListener={!compressed && !item.locked}>
							<Box sx={{ position: 'relative', padding: `0 ${compressed ? 4 : 20}px` }}>
								{isActive && !compressed && (
									<Box
									sx={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: 6,
										height: '100%',
										borderRadius: '0 4px 4px 0',
										bgcolor: 'var(--color-primary)',
									}}
									/>
								)}
								<ListItemButton
									sx={{
										pl: compressed ? 2 : 2 + depth * 4,
										borderRadius: 2,
										mb: 0.5,
										minHeight: depth > 0 ? 30 : 50,
										justifyContent: compressed ? 'center' : 'flex-start',
										bgcolor: isActive ? activeBg : 'transparent',
										color: isActive ? activeColor : inactiveColor,
										opacity: item.locked && !isActive ? 0.5 : 1,
										boxShadow: isActive ? '0 2px 8px 0 rgba(var(--color-primary-rgb), 0.08)' : 'none',
										'&:hover': {
											bgcolor: isActive ? activeBg : location.pathname.includes('admin') ? '#ffffff29' : 'var(--color-container-background)',
										},
										transition: 'background 0.2s'
									}}
									onClick={(e) => {
										if (hasChildren) {
											if (compressed) handleCompressedMenuClick(e, item.children!);
											else handleToggle(item.label);
										} else if (item.path) navigate(item.path);
									}}
								>
									{depth === 0 &&
										<ListItemIcon 
											sx={{ 
												minWidth: 0, mr: compressed ? 0 : 2, 
												justifyContent: 'center', 
												color: isActive ? activeColor : inactiveColor 
											}}
										>
											<IconComponent fontSize="medium" />
										</ListItemIcon>
									}
									{!compressed && 
										<ListItemText
											primary={item.label}
											primaryTypographyProps={{
												color: isActive ? activeColor : inactiveColor,
												fontWeight: isActive ? 600 : 400,
												fontSize: depth > 0 ? 14 : 16,
												sx: {
													fontSize: `${depth > 0 ? 14 : 16}px !important`,
													color: isActive ? activeColor : inactiveColor,
													fontWeight: isActive ? 600 : 400,
												}
											}}
										/>
									}
									{item.locked && !compressed && <Lock size={14} style={{ marginLeft: 8, flexShrink: 0 }} />}
									{hasChildren && !compressed && (openMenus[item.label] ? <ExpandLess sx={{ color: isActive ? activeColor : inactiveColor }} /> : <ExpandMore sx={{ color: isActive ? activeColor : inactiveColor }} />)}
								</ListItemButton>
							</Box>
						</Tooltip>
						{hasChildren && !compressed && (
							<Collapse in={!!openMenus[item.label]} timeout="auto" unmountOnExit>
								{renderMenu(item.children!, depth + 1)}
							</Collapse>
						)}
					</React.Fragment>
				);
			})}
		</List>
	);

	// Render popup menu for compressed mode
	const renderPopupMenu = () => {
		if (!popupMenu) return null;
		return (
			<Box
				sx={{
					position: 'fixed',
					top: popupMenu.anchorEl?.getBoundingClientRect().top ?? 0,
					left: SIDEBAR_WIDTH_COMPRESSED,
					bgcolor: 'background.paper',
					boxShadow: 3,
					minWidth: 180,
					zIndex: 2000,
				}}
				onMouseLeave={handlePopupClose}
			>
				<List>
					{popupMenu.children.map((child) => (
						<ListItemButton
							key={child.label}
							onClick={() => {
								if (child.path) navigate(child.path);
								handlePopupClose();
							}}
						>
							<ListItemText primary={child.label} />
						</ListItemButton>
					))}
				</List>
			</Box>
		);
	};

	// Sidebar layout
	return (
		<SidebarContext.Provider value={{ compressed, setCompressed }}>
			<>
				<Box
					sx={{
						width: compressed ? SIDEBAR_WIDTH_COMPRESSED : SIDEBAR_WIDTH,
						height: '100vh',
						display: 'flex',
						flexDirection: 'column',
						bgcolor: bgColor,
						position: 'fixed',
						top: 0,
						left: 0,
						zIndex: 1200,
						transition: 'width 0.2s',
					}}
				>
					{/* Top: Logo/Title and toggle */}
					<Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: compressed ? 0 : 1, justifyContent: 'center', position: 'relative', minHeight: 30 }}>
						   {compressed ? (
							   <IconButton
								   onClick={() => setCompressed((c) => !c)}
								   size="small"
								   sx={{
									   background: bgColor,
									   border: '1px solid #eee',
									   boxShadow: 1,
									   width: 40,
									   height: 40,
									   '&:hover': { background: isAdmin ? '#ffffff28' : 'var(--color-container-background)' },
								   }}
							   >
								   <ChevronRight color={inactiveColor } />
							   </IconButton>
						   ) : (
							   <>
								   <img 
										src={isAdmin || isDark ? logoDarkImage : logoImage}
										alt="Logo" 
										style={{ width: 128, height: 32, padding: '0 12px' }} 
										onClick={() => {
											if (location.pathname === '/transaction') {
												window.location.reload();
											} else {
												!isAdmin && navigate('/transaction');
											}
										}} 
									/>
								   <IconButton
									   onClick={() => setCompressed((c) => !c)}
									   size="small"
									   sx={{
										   position: 'absolute',
										   right: -20,
										   top: '50%',
										   transform: 'translateY(-50%)',
										   background: bgColor,
										   border: '1px solid #eee',
										   boxShadow: 1,
										   zIndex: 2,
										   width: 40,
										   height: 40,
										   transition: 'right 0.2s',
										   '&:hover': { background: isAdmin ? '#ffffff28' : 'var(--color-container-background)' },
									   }}
								   >
									   <ChevronLeft color={inactiveColor } />
								   </IconButton>
							   </>
						   )}
					   </Box>
					{/* Menus */}
					<Box sx={{ flex: 1, overflowY: 'auto', mt: 1, overflowX: 'hidden' }}>
						{renderMenu(
							isAdmin
								? filterByFeature(adminMenuData as MenuItem[])
								: filterByPermission(filterByFeature(menuData as MenuItem[]))
						)}
					</Box>
				</Box>
				{compressed && renderPopupMenu()}
			</>
		</SidebarContext.Provider>
	);
};

export default Sidebar;
